import React, { useState } from "react";
import {
  Image as ImageIcon,
  VideoCamera,
  Cards,
  PhoneCall,
  TagSimple,
  CalendarCheck,
  Trophy,
  Sparkle,
  X,
  CircleNotch,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import { shareImage, shareVideo, shareFiles } from "@/lib/share";

/**
 * QuickShareSheet — bottom sheet "Crea post veloce" attivabile dalla card
 * di ogni annuncio. Genera in 1-2 tap un contenuto social pronto da
 * condividere via share sheet iOS (WhatsApp / IG / FB / Telegram / Mail).
 *
 * Riusa al 100% gli endpoint backend esistenti del Social Studio:
 *   - Post quadrato:    GET /api/properties/{pid}/social/post.png?aspect=1x1
 *   - Story 9:16:       GET /api/properties/{pid}/social/post.png?aspect=9x16
 *   - Reel:             GET /api/properties/{pid}/social/reel.mp4
 *   - Carosello (zip):  GET /api/properties/{pid}/social/carousel.zip
 *   - Prezzo ridotto:   GET .../post.png?occasion=prezzo_nuovo&new_price=...
 *   - Open house:       GET .../post.png?occasion=open_house&event_date=...&event_time=...
 *   - Venduto:          GET .../post.png?occasion=venduto&days_to_sell=...
 *
 * Per le 3 occasioni "speciali" che richiedono input extra (nuovo prezzo,
 * data/ora open house, giorni venduto) mostriamo un mini-form inline
 * appena tappato il tile, prima di lanciare la generazione.
 */
export default function QuickShareSheet({ property, open, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // null | <tileId>
  const [needsInput, setNeedsInput] = useState(null); // null | "prezzo_nuovo" | "open_house" | "venduto"
  const [inputData, setInputData] = useState({});

  if (!open || !property) return null;

  const pid = property.id;
  const baseUrl = `${BACKEND_URL}/api/properties/${pid}/social`;

  const closeAll = () => {
    setNeedsInput(null);
    setInputData({});
    setBusy(null);
    onClose();
  };

  const generatePost = async ({ url, filename, title, isVideo, isCarousel }) => {
    setBusy(filename);
    try {
      const fullTitle = title || property.title || "Annuncio Pronto";
      const text = `${fullTitle}${property.city ? ` · ${property.city}` : ""}`;
      if (isCarousel) {
        // Per il carosello ZIP non c'è uno share sheet diretto: scarichiamo
        // lo zip e poi avvisiamo l'utente di estrarlo e caricarlo manualmente.
        // Su iOS questo apre il file manager → IG/WhatsApp leggono lo zip.
        await shareFiles({
          files: [{ url, filename, mime: "application/zip" }],
          title: fullTitle, text,
        });
      } else if (isVideo) {
        await shareVideo({ url, filename, title: fullTitle, text });
      } else {
        await shareImage({ url, filename, title: fullTitle, text });
      }
      closeAll();
    } catch (e) {
      // shareFiles/Video/Image gestiscono già i toast interni — non aggiungere
      // un secondo toast qui per evitare il doppio rumore.
      if (e?.name !== "AbortError") {
        toast.error("Generazione non riuscita");
      }
    } finally {
      setBusy(null);
    }
  };

  // ─── Tile handler ────────────────────────────────────────────────────────

  const onPost = () =>
    generatePost({
      url: `${baseUrl}/post.png?aspect=1x1`,
      filename: `pronto-post-${property.id.slice(0, 8)}.png`,
      title: property.title,
    });

  const onReel = () =>
    generatePost({
      url: `${baseUrl}/reel.mp4`,
      filename: `pronto-reel-${property.id.slice(0, 8)}.mp4`,
      title: property.title,
      isVideo: true,
    });

  const onCarousel = () =>
    generatePost({
      url: `${baseUrl}/carousel.zip`,
      filename: `pronto-carosello-${property.id.slice(0, 8)}.zip`,
      title: property.title,
      isCarousel: true,
    });

  const onStory = () =>
    generatePost({
      url: `${baseUrl}/post.png?aspect=9x16`,
      filename: `pronto-story-${property.id.slice(0, 8)}.png`,
      title: property.title,
    });

  const onPriceReduced = () => {
    if (property.hide_price) {
      toast.error("Disattiva «trattativa riservata» su questo annuncio per usare il post «Prezzo ridotto».");
      return;
    }
    setNeedsInput("prezzo_nuovo");
    setInputData({ new_price: "" });
  };

  const submitPriceReduced = () => {
    const np = parseFloat((inputData.new_price || "").toString().replace(/[^\d.]/g, ""));
    if (!np || np <= 0) {
      toast.error("Inserisci il nuovo prezzo (numero in euro)");
      return;
    }
    setNeedsInput(null);
    generatePost({
      url: `${baseUrl}/post.png?occasion=prezzo_nuovo&new_price=${np}&show_price=true`,
      filename: `pronto-prezzo-ridotto-${property.id.slice(0, 8)}.png`,
      title: property.title,
    });
  };

  const onOpenHouse = () => {
    setNeedsInput("open_house");
    // Default: prossimo sabato alle 15:00
    const next = new Date();
    next.setDate(next.getDate() + ((6 - next.getDay() + 7) % 7 || 7));
    setInputData({
      event_date: next.toISOString().slice(0, 10),
      event_time: "15:00",
    });
  };

  const submitOpenHouse = () => {
    if (!inputData.event_date || !inputData.event_time) {
      toast.error("Inserisci data e ora dell'open house");
      return;
    }
    setNeedsInput(null);
    const qs = new URLSearchParams({
      occasion: "open_house",
      event_date: inputData.event_date,
      event_time: inputData.event_time,
    }).toString();
    generatePost({
      url: `${baseUrl}/post.png?${qs}`,
      filename: `pronto-open-house-${property.id.slice(0, 8)}.png`,
      title: `Open House · ${property.title || "Immobile"}`,
    });
  };

  const onSold = () => {
    setNeedsInput("venduto");
    setInputData({ days_to_sell: "", mode: "venduto" });
  };

  const submitSold = () => {
    const dts = parseInt(inputData.days_to_sell, 10);
    if (!dts || dts <= 0 || dts > 999) {
      toast.error("Inserisci in quanti giorni è stato venduto/affittato (1-999)");
      return;
    }
    const isRent = inputData.mode === "affittato";
    setNeedsInput(null);
    const qs = new URLSearchParams({
      occasion: "venduto",
      days_to_sell: String(dts),
    }).toString();
    generatePost({
      url: `${baseUrl}/post.png?${qs}${isRent ? "&label_override=AFFITTATO" : ""}`,
      filename: `pronto-${isRent ? "affittato" : "venduto"}-${property.id.slice(0, 8)}.png`,
      title: `${isRent ? "Affittato" : "Venduto"} · ${property.title || "Immobile"}`,
    });
  };

  const onOpenEditor = () => {
    onClose();
    navigate(`/dashboard/property/${pid}?tab=social`);
  };

  // ─── Tile definitions ────────────────────────────────────────────────────

  const tiles = [
    { id: "post",      label: "Post",          icon: ImageIcon,     hint: "1080×1080",            color: "#7C8B6E", onClick: onPost },
    { id: "reel",      label: "Reel",          icon: VideoCamera,   hint: "Video verticale 9:16", color: "#7C8B6E", onClick: onReel },
    { id: "carousel",  label: "Carosello",     icon: Cards,         hint: "Più foto",             color: "#7C8B6E", onClick: onCarousel },
    { id: "story",     label: "Story",         icon: PhoneCall,     hint: "Verticale 24h",        color: "#7C8B6E", onClick: onStory },
    { id: "prezzo",    label: "Prezzo ridotto", icon: TagSimple,    hint: "Badge rosso",           color: "#C66060", onClick: onPriceReduced },
    { id: "openhouse", label: "Open house",    icon: CalendarCheck, hint: "Data + ora",            color: "#C49A4A", onClick: onOpenHouse },
    { id: "venduto",   label: "Venduto",       icon: Trophy,        hint: "/ Affittato",            color: "#1C1C1A", onClick: onSold },
    { id: "edit",      label: "Personalizza",  icon: Sparkle,       hint: "Editor completo",      color: "#9A968B", onClick: onOpenEditor },
  ];

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Chiudi"
        onClick={closeAll}
        className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
        data-testid="quick-share-backdrop"
      />

      {/* Sheet */}
      <div
        className="fixed inset-x-0 bottom-0 z-[81] bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
        data-testid="quick-share-sheet"
      >
        {/* Handle bar iOS-style */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#D8D5CD]" />
        </div>

        {/* Header */}
        <div className="px-5 pb-3 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Crea post</div>
            <div className="text-[15px] font-semibold text-[#1C1C1A] mt-0.5">
              {property.title || "Annuncio"}
            </div>
            <div className="text-[11.5px] text-[#9A968B]">
              {property.city || ""}
              {property.price ? ` · € ${Math.round(property.price).toLocaleString("it-IT")}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={closeAll}
            className="w-8 h-8 rounded-full bg-[#F4F1EA] hover:bg-[#EAE7DF] flex items-center justify-center transition-colors flex-shrink-0 appearance-none p-0"
            style={{ width: 32, height: 32, minWidth: 32, minHeight: 32 }}
            aria-label="Chiudi"
            data-testid="quick-share-close"
          >
            <X size={14} weight="bold" className="text-[#1C1C1A]" />
          </button>
        </div>

        {/* Mini-form inline per occasioni con parametri */}
        {needsInput === "prezzo_nuovo" && (
          <PriceReducedForm
            value={inputData.new_price}
            onChange={(v) => setInputData({ new_price: v })}
            currentPrice={property.price}
            onCancel={() => setNeedsInput(null)}
            onSubmit={submitPriceReduced}
          />
        )}
        {needsInput === "open_house" && (
          <OpenHouseForm
            data={inputData}
            onChange={(d) => setInputData({ ...inputData, ...d })}
            onCancel={() => setNeedsInput(null)}
            onSubmit={submitOpenHouse}
          />
        )}
        {needsInput === "venduto" && (
          <SoldForm
            data={inputData}
            onChange={(d) => setInputData({ ...inputData, ...d })}
            onCancel={() => setNeedsInput(null)}
            onSubmit={submitSold}
          />
        )}

        {/* Grid 4×2 — i 4 formati in alto, le 4 occasioni in basso */}
        {!needsInput && (
          <>
            <div className="px-5 pt-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#9A968B] font-semibold mb-2">Formati</div>
              <div className="grid grid-cols-4 gap-2.5">
                {tiles.slice(0, 4).map((t) => (
                  <TileButton key={t.id} tile={t} busy={busy === t.id || busy?.includes?.(t.id)} />
                ))}
              </div>
            </div>

            <div className="px-5 pt-5 pb-4">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#9A968B] font-semibold mb-2">Occasioni</div>
              <div className="grid grid-cols-4 gap-2.5">
                {tiles.slice(4, 8).map((t) => (
                  <TileButton key={t.id} tile={t} busy={busy === t.id || busy?.includes?.(t.id)} />
                ))}
              </div>
            </div>

            <div className="px-5 pb-2 text-center">
              <div className="text-[10.5px] text-[#9A968B] leading-relaxed">
                Ogni post consuma crediti come dall&apos;editor.
                Si apre lo share di iOS appena pronto.
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Tile component ────────────────────────────────────────────────────────

function TileButton({ tile, busy }) {
  const Icon = tile.icon;
  return (
    <button
      type="button"
      onClick={tile.onClick}
      disabled={busy}
      className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl bg-[#FAFAF7] border border-[#EAE7DF] hover:bg-[#F1F4ED] hover:border-[#7C8B6E]/30 active:scale-95 transition-all disabled:opacity-50 disabled:active:scale-100 appearance-none"
      data-testid={`quick-share-tile-${tile.id}`}
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: `${tile.color}15`, minWidth: 44, minHeight: 44 }}
      >
        {busy ? (
          <CircleNotch size={20} weight="bold" className="animate-spin" style={{ color: tile.color }} />
        ) : (
          <Icon size={20} weight="bold" style={{ color: tile.color }} />
        )}
      </div>
      <div className="text-center">
        <div className="text-[11.5px] font-semibold text-[#1C1C1A] leading-tight">{tile.label}</div>
        <div className="text-[9.5px] text-[#9A968B] leading-tight mt-0.5">{tile.hint}</div>
      </div>
    </button>
  );
}

// ─── Mini-forms ────────────────────────────────────────────────────────────

function PriceReducedForm({ value, onChange, currentPrice, onCancel, onSubmit }) {
  const suggested = currentPrice ? Math.round(currentPrice * 0.9) : null;
  return (
    <div className="px-5 py-4 border-t border-[#EAE7DF] mt-2">
      <div className="text-[13px] font-semibold text-[#1C1C1A] mb-1">Qual è il nuovo prezzo?</div>
      {currentPrice && (
        <div className="text-[11.5px] text-[#9A968B] mb-3">
          Prezzo attuale: € {Math.round(currentPrice).toLocaleString("it-IT")}
          {suggested && ` · Suggerito (-10%): € ${suggested.toLocaleString("it-IT")}`}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="number"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={suggested ? String(suggested) : "es. 245000"}
          className="flex-1 px-3 py-2.5 rounded-xl border border-[#EAE7DF] text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7C8B6E]/30"
          autoFocus
          data-testid="quick-share-new-price-input"
        />
      </div>
      <FormButtons onCancel={onCancel} onSubmit={onSubmit} submitLabel="Crea post" />
    </div>
  );
}

function OpenHouseForm({ data, onChange, onCancel, onSubmit }) {
  return (
    <div className="px-5 py-4 border-t border-[#EAE7DF] mt-2">
      <div className="text-[13px] font-semibold text-[#1C1C1A] mb-3">Quando è l&apos;open house?</div>
      <div className="grid grid-cols-2 gap-2 mb-1">
        <div>
          <label className="text-[10px] uppercase tracking-[0.12em] text-[#9A968B] font-semibold block mb-1">Data</label>
          <input
            type="date"
            value={data.event_date || ""}
            onChange={(e) => onChange({ event_date: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-[#EAE7DF] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7C8B6E]/30"
            data-testid="quick-share-open-house-date"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.12em] text-[#9A968B] font-semibold block mb-1">Ora</label>
          <input
            type="time"
            value={data.event_time || ""}
            onChange={(e) => onChange({ event_time: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-[#EAE7DF] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7C8B6E]/30"
            data-testid="quick-share-open-house-time"
          />
        </div>
      </div>
      <FormButtons onCancel={onCancel} onSubmit={onSubmit} submitLabel="Crea post" />
    </div>
  );
}

function SoldForm({ data, onChange, onCancel, onSubmit }) {
  return (
    <div className="px-5 py-4 border-t border-[#EAE7DF] mt-2">
      <div className="text-[13px] font-semibold text-[#1C1C1A] mb-3">Operazione conclusa</div>
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => onChange({ mode: "venduto" })}
          className={`flex-1 py-2.5 rounded-xl text-[13px] font-semibold transition-all ${
            data.mode === "venduto"
              ? "bg-[#1C1C1A] text-white"
              : "bg-[#F4F1EA] text-[#1C1C1A]"
          }`}
          data-testid="quick-share-sold-mode-venduto"
        >
          Venduto
        </button>
        <button
          type="button"
          onClick={() => onChange({ mode: "affittato" })}
          className={`flex-1 py-2.5 rounded-xl text-[13px] font-semibold transition-all ${
            data.mode === "affittato"
              ? "bg-[#1C1C1A] text-white"
              : "bg-[#F4F1EA] text-[#1C1C1A]"
          }`}
          data-testid="quick-share-sold-mode-affittato"
        >
          Affittato
        </button>
      </div>
      <label className="text-[10px] uppercase tracking-[0.12em] text-[#9A968B] font-semibold block mb-1">In quanti giorni?</label>
      <input
        type="number"
        inputMode="numeric"
        value={data.days_to_sell || ""}
        onChange={(e) => onChange({ days_to_sell: e.target.value })}
        placeholder="es. 14"
        className="w-full px-3 py-2.5 rounded-xl border border-[#EAE7DF] text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7C8B6E]/30"
        autoFocus
        data-testid="quick-share-sold-days-input"
      />
      <FormButtons onCancel={onCancel} onSubmit={onSubmit} submitLabel="Crea post" />
    </div>
  );
}

function FormButtons({ onCancel, onSubmit, submitLabel }) {
  return (
    <div className="flex gap-2 mt-4">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 py-2.5 rounded-xl bg-[#F4F1EA] text-[#1C1C1A] text-[14px] font-semibold hover:bg-[#EAE7DF] transition-colors"
        data-testid="quick-share-form-cancel"
      >
        Annulla
      </button>
      <button
        type="button"
        onClick={onSubmit}
        className="flex-1 py-2.5 rounded-xl bg-[#7C8B6E] text-white text-[14px] font-semibold hover:bg-[#6B7A5E] transition-colors"
        data-testid="quick-share-form-submit"
      >
        {submitLabel}
      </button>
    </div>
  );
}
